import { useMemo, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOSchema from '@/components/feature/SEOSchema';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9ork82qs38sj6jc7e7g';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('submitting');
    setFormError('');

    const formData = new FormData(form);

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          params.append(key, value as string);
        }
      });
      const resp = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试。');
    }
  };

  const organizationSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网',
    url: 'https://thingschat.cn',
    description: '韩漫网提供综合服务，实现韩漫在线观看与漫画免费下拉式阅读。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@thingschat.cn',
    },
  }), []);

  const webpageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩漫网',
    description: '了解韩漫网的品牌故事、用户口碑与联系方式',
    url: 'https://thingschat.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'https://thingschat.cn',
    },
  }), []);

  const reviews = [
    { name: '王**', rating: 5, content: '韩漫网真的太棒了！每天都在这里追更，下拉式阅读体验超级舒服，漫画种类也多，强烈推荐！', date: '2026-07-28' },
    { name: '李**', rating: 5, content: '用了这么多漫画平台，还是韩漫网最顺手。画质高清，加载快，最重要的是完全免费，良心平台！', date: '2026-07-25' },
    { name: '张**', rating: 4, content: '漫画内容很丰富，更新也很及时。下拉式阅读确实比翻页舒服很多，已经推荐给朋友了。', date: '2026-07-22' },
    { name: '陈**', rating: 5, content: '从大学开始就在韩漫网看漫画，三年了，漫画库越来越全，真心觉得这是一个很用心的平台。', date: '2026-07-18' },
    { name: '刘**', rating: 5, content: '韩漫网的分类做得特别好，找漫画很方便。推荐和排行功能也帮我发现了很多好看的新漫画。', date: '2026-07-15' },
    { name: '赵**', rating: 4, content: '作为韩漫爱好者，韩漫网满足了我的所有需求。希望以后能增加更多完结漫画的全集资源。', date: '2026-07-10' },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="Organization" data={organizationSchema} />
      <SEOSchema type="WebPage" data={webpageSchema} />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <section id="brand" className="mt-6">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              关于韩漫网
            </h1>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-lg font-semibold font-heading text-foreground-800">品牌介绍</h2>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  韩漫网成立于2020年，是国内领先的韩国漫画在线阅读平台。我们致力于为广大韩漫爱好者提供
                  <strong>韩漫在线观看</strong>与<strong>漫画免费下拉式阅读</strong>服务，收录
                  <strong>好看韩国漫画全集</strong>，涵盖恋爱、奇幻、动作、悬疑等十二大热门类型。
                </p>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  韩漫网提供综合服务，以用户阅读体验为核心，采用先进的下拉式阅读技术，让漫画阅读更加流畅自然。我们与多家韩国漫画版权方合作，确保内容的丰富性与更新时效性，每日更新热门连载，为用户带来最新最快的韩漫阅读体验。
                </p>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  截至2026年，韩漫网已收录超过12,800部韩国漫画，累计服务580万+读者，成为中文互联网上极具影响力的韩漫阅读平台。
                </p>

                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-background-100 text-center">
                    <div className="text-2xl font-bold font-heading text-primary-600">12800+</div>
                    <p className="mt-1 text-xs text-foreground-500">收录漫画</p>
                  </div>
                  <div className="p-4 rounded-xl bg-background-100 text-center">
                    <div className="text-2xl font-bold font-heading text-accent-600">580万+</div>
                    <p className="mt-1 text-xs text-foreground-500">服务读者</p>
                  </div>
                  <div className="p-4 rounded-xl bg-background-100 text-center">
                    <div className="text-2xl font-bold font-heading text-primary-600">5年+</div>
                    <p className="mt-1 text-xs text-foreground-500">运营时长</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden bg-background-200 h-64 lg:h-auto">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20reading%20platform%20office%20environment%2C%20modern%20cozy%20workspace%20with%20digital%20tablets%20displaying%20comics%2C%20warm%20lighting%2C%20creative%20atmosphere%2C%20clean%20minimalist%20design%2C%20professional%20setting&width=800&height=600&seq=manhwa-about-brand&orientation=landscape"
                  alt="韩漫网品牌形象"
                  title="韩漫网 - 韩国漫画在线阅读平台"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </section>

          <section id="reviews" className="mt-16">
            <h2 className="text-xl font-bold font-heading text-foreground-900">用户口碑</h2>
            <p className="mt-2 text-sm text-foreground-500">来自读者的真实评价</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.map((review, index) => (
                <div key={index} className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i
                          key={s}
                          className={`text-xs w-3 h-3 flex items-center justify-center ${
                            s <= review.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                  <p className="mt-3 text-xs text-foreground-400">{review.date}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="faq" className="mt-16">
            <h2 className="text-xl font-bold font-heading text-foreground-900">常见问题</h2>
            <div className="mt-6 space-y-3">
              {[
                { q: '韩漫网的漫画来源是什么？', a: '韩漫网收录的漫画内容来源于合作的韩国漫画版权方及公开授权资源。我们尊重知识产权，如涉及版权问题，请通过联系方式与我们沟通。' },
                { q: '如何收藏喜欢的漫画？', a: '在漫画详情页点击"收藏"按钮即可将漫画加入您的收藏列表，方便随时追更。' },
                { q: '韩漫网有手机APP吗？', a: '目前韩漫网为网页版平台，已针对手机浏览器进行优化，支持移动端下拉式阅读，体验与APP无异。' },
              ].map((faq, i) => (
                <details key={i} className="group rounded-xl bg-background-100 border border-background-200/70">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <h3 className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</h3>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section id="contact" className="mt-16">
            <h2 className="text-xl font-bold font-heading text-foreground-900">联系我们</h2>
            <p className="mt-2 text-sm text-foreground-500">有任何问题或建议，欢迎通过以下方式联系我们</p>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                    <p className="text-sm text-foreground-500">contact@thingschat.cn</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">微信公众号</p>
                    <p className="text-sm text-foreground-500">韩漫网官方</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">工作时间</p>
                    <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <div>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-accent-600 text-center">{formError || '提交失败，请稍后再试'}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}